import type { EndpointBuilder } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CrashDocsLookupResponse,
  CrashDocsLookupStatusResponse,
} from '../types/client-intake-types';
import { PoliceReportLookupListResponse } from '../types/police-report-types';
import { baseQueryWithErrorHandling } from './baseQuery';
import { enhancedApi as generatedApi } from './generated/policeReportsService';

export interface CrashReportLookupParams {
  taskId: string;
  data: {
    state: string;
    jurisdiction: string;
    last_name: string;
    incident_date: string;
    report_id?: string;
  };
}

export const policeReportsApi = createApi({
  reducerPath: 'policeReportsApi',
  baseQuery: baseQueryWithErrorHandling,
  endpoints: (
    builder: EndpointBuilder<typeof baseQueryWithErrorHandling, any, any>,
  ) => ({
    lookupCrashDocs: builder.mutation<
      CrashDocsLookupResponse,
      CrashReportLookupParams
    >({
      query: ({ taskId, data }: CrashReportLookupParams) => ({
        url: `api/tasks/${taskId}/police-report-lookup`,
        method: 'POST',
        body: data,
      }),
    }),

    // New endpoint to check the status of a police report lookup
    getCrashDocsLookupStatus: builder.query<
      CrashDocsLookupStatusResponse,
      string
    >({
      query: (lookupId: string) => ({
        url: `api/police-reports/lookups/${lookupId}/status`,
        method: 'GET',
      }),
    }),

    // New endpoint to list police report lookups
    listPoliceReportLookups: builder.query<
      PoliceReportLookupListResponse,
      void
    >({
      query: () => ({
        url: 'api/police-reports/lookups/',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useLookupCrashDocsMutation,
  useGetCrashDocsLookupStatusQuery,
  useListPoliceReportLookupsQuery,
} = policeReportsApi;

/**
 * Enhanced version of generated API with better cache invalidation
 * Provides specific tags per matter for efficient cache management
 */
export const enhancedPoliceReportsApi = generatedApi
  .injectEndpoints({
    endpoints: (build) => ({
      // Discover police reports with proper types (override incomplete codegen)
      discoverPoliceReports: build.mutation<
        {
          suggestions: unknown[];
          validations: unknown[];
          applied: unknown[];
          message?: string;
        },
        { matterId: string }
      >({
        query: ({ matterId }) => ({
          url: `/api/matters/${matterId}/police-reports/discover/`,
          method: 'POST',
        }),
        invalidatesTags: (result, error, { matterId }) => [
          { type: 'police_reports' as const, id: matterId },
          'police_reports',
        ],
      }),
    }),
    overrideExisting: true,
  })
  .enhanceEndpoints({
    endpoints: {
      // Mutations that modify police reports
      policeReportsPartialUpdate: {
        invalidatesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          { type: 'police_reports' as const, id: `${arg.matterId}-${arg.id}` },
          'police_reports',
        ],
      },
      policeReportsUpdate: {
        invalidatesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          { type: 'police_reports' as const, id: `${arg.matterId}-${arg.id}` },
          'police_reports',
        ],
      },
      policeReportsCreate: {
        invalidatesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          'police_reports',
        ],
      },
      policeReportsDestroy: {
        invalidatesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          'police_reports',
        ],
      },
      policeReportsTriggerOrchestration: {
        invalidatesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          'police_reports',
        ],
      },
      policeReportsEnsureTasks: {
        invalidatesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          'police_reports',
        ],
      },
      policeReportsStartBrowserAgent: {
        invalidatesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          { type: 'police_reports' as const, id: `${arg.matterId}-${arg.id}` },
          'police_reports',
        ],
      },
      policeReportsUpdateAttestationStatus: {},
      // Queries that fetch police reports
      policeReportsList: {
        providesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          'police_reports',
        ],
      },
      policeReportsRetrieve: {
        providesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: `${arg.matterId}-${arg.id}` },
          { type: 'police_reports' as const, id: arg.matterId },
          'police_reports',
        ],
      },
      policeReportsGetWithAutomationData: {
        providesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: `${arg.matterId}-${arg.id}` },
          { type: 'police_reports' as const, id: arg.matterId },
          'police_reports',
        ],
      },
      policeReportsGetBrowserRuns: {
        providesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: `${arg.matterId}-${arg.id}` },
          'police_reports',
        ],
      },
      policeReportsMatterSnapshot: {
        providesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          'police_reports',
        ],
      },
      policeReportsGetOutreachSummary: {
        providesTags: (result, error, arg) => [
          { type: 'police_reports' as const, id: arg.matterId },
          'police_reports',
        ],
      },
    },
  });

// Export all hooks from enhanced API
export const {
  // Queries
  usePoliceReportsListQuery,
  usePoliceReportsRetrieveQuery,
  usePoliceReportsGetWithAutomationDataQuery,
  usePoliceReportsGetBrowserRunsQuery,
  usePoliceReportsMatterSnapshotQuery,
  usePoliceReportsGetOutreachSummaryQuery,
  // Mutations
  usePoliceReportsCreateMutation,
  usePoliceReportsUpdateMutation,
  usePoliceReportsPartialUpdateMutation,
  usePoliceReportsDestroyMutation,
  usePoliceReportsUpdateAttestationStatusMutation,
  usePoliceReportsEnsureTasksMutation,
  useDiscoverPoliceReportsMutation,
  usePoliceReportsTriggerOrchestrationMutation,
  usePoliceReportsStartBrowserAgentMutation,
  usePoliceDepartmentsFireBrowserRunMutation,
  usePoliceDepartmentsListBrowserRunsQuery,
} = enhancedPoliceReportsApi;

/**
 * DEPRECATED: Generate FOIA letter endpoint moved to investigation_records_requests
 * Use the new service instead:
 * @see /services/api/investigationRecordsRequestsService.ts
 *
 * New endpoint: GET /api/matters/{matterId}/investigation-records-requests/generate-foia-letter/
 *
 * This file remains for backwards compatibility only.
 */
